// src/components/AdminLayout.js
import React, { useState, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  FiGrid,
  FiCalendar,
  FiEdit3,
  FiUsers,
  FiTag,
  FiClipboard,
  FiLogOut,
} from "react-icons/fi";
import { FaUserMd } from "react-icons/fa";

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const displayName = user?.name || user?.username || "Admin";
  const close = () => setOpen(false);

  // ✅ Desktop rule:
  // - Dashboard => big sidebar
  // - Other admin pages => icon-only sidebar
  const isDashboard = (location.pathname || "").startsWith("/admin/dashboard");

  // ✅ show current page name in mobile top bar
  const pageTitle = useMemo(() => {
    const p = location.pathname || "";
    if (p.startsWith("/admin/dashboard")) return "Dashboard";
    if (p.startsWith("/admin/calendar")) return "Calendar";
    if (p.startsWith("/admin/schedule")) return "Schedule";
    if (p.startsWith("/admin/staff")) return "Staff";
    if (p.startsWith("/admin/departments")) return "Departments";
    if (p.startsWith("/team-schedule")) return "Team Schedule";
    if (p.startsWith("/admin/attendance-today")) return "Today's Attendance";
    return "Menu";
  }, [location.pathname]);

  const sidebarLinkClass = ({ isActive }) => `adm-side-item ${isActive ? "active" : ""}`;
  const slimLinkClass = ({ isActive }) => `adm-slim-item ${isActive ? "active" : ""}`;

  return (
    <div className={`adm-shell ${isDashboard ? "wide" : "slim"}`}>
      <style>{`
        :root{
          --adm-bg:#f3f6fb;
          --adm-card:#ffffff;
          --adm-text:#0f172a;
          --adm-muted:#64748b;
          --adm-line: rgba(2, 6, 23, .10);
          --adm-shadow: 0 10px 22px rgba(2, 6, 23, .06);
          --adm-blue:#2563eb;

          --adm-side-wide: 260px;
          --adm-side-slim: 74px;
        }

        .adm-shell{
          min-height: 100dvh;
          background: var(--adm-bg);
          width: 100%;
          overflow-x: hidden;
        }

        /* ================= DESKTOP BIG SIDEBAR (DASHBOARD ONLY) ================= */
        .adm-side-wide{
          position: fixed;
          top: 0;
          left: 0;
          width: var(--adm-side-wide);
          height: 100dvh;
          background: var(--adm-card);
          border-right: 1px solid var(--adm-line);
          padding: 18px 14px;
          display: flex;
          flex-direction: column;
          z-index: 40;
          overflow: hidden;
        }

        .adm-side-logo{
          display:flex;
          align-items:center;
          gap: 12px;
          padding: 10px 10px 16px;
          flex: 0 0 auto;
        }

        .adm-logo-wrap{
          width: 56px;
          height: 56px;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 10px 18px rgba(37,99,235,.18);
          background: #fff;
          flex: 0 0 auto;
        }

        .adm-logo-img{
          width: 100%;
          height: 100%;
          border-radius: 12px;
          object-fit: cover;
          object-position: center;
          transform: scale(4.25);
          transform-origin: 50% 24%;
        }

        .adm-logo-text{
          font-weight: 950;
          font-size: 22px;
          letter-spacing: .2px;
          color: var(--adm-text);
        }
        .adm-logo-text span{ color: var(--adm-blue); }

        .adm-side-scroll{
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding-bottom: 10px;
          scrollbar-width: none;
        }
        .adm-side-scroll::-webkit-scrollbar{ width: 0; height: 0; }

        .adm-side-item{
          display:flex;
          align-items:center;
          gap: 10px;
          padding: 11px 12px;
          margin: 6px 6px;
          border-radius: 12px;
          color: var(--adm-text);
          text-decoration: none;
          font-weight: 900;
          border: 1px solid transparent;
          transition: background .16s ease, border-color .16s ease, transform .16s ease;
        }
        .adm-side-item:hover{
          background: rgba(37, 99, 235, .06);
          transform: translateY(-1px);
        }
        .adm-side-item.active{
          background: rgba(37, 99, 235, .10);
          color: var(--adm-blue);
          border: 1px solid rgba(37, 99, 235, .14);
        }

        .adm-side-ico{
          width: 32px;
          height: 32px;
          display: inline-flex;
          justify-content: center;
          align-items: center;
          font-size: 16px;
          line-height: 1;
          flex: 0 0 32px;
          border-radius: 11px;
        }

        .adm-tile-blue{
          background: rgba(37,99,235,.14);
          color: #2563eb;
        }
        .adm-tile-amber{
          background: rgba(245,158,11,.18);
          color: #d97706;
        }
        .adm-tile-cyan{
          background: rgba(6,182,212,.16);
          color: #0891b2;
        }
        .adm-tile-purple{
          background: rgba(124,58,237,.16);
          color: #7c3aed;
        }
        .adm-tile-teal{
          background: rgba(20,184,166,.16);
          color: #0f766e;
        }
        .adm-tile-indigo{
          background: rgba(79,70,229,.16);
          color: #4338ca;
        }
        .adm-tile-violet{
          background: rgba(139,92,246,.16);
          color: #7c3aed;
        }
        .adm-tile-red{
          background: rgba(239,68,68,.14);
          color: #dc2626;
        }

        .adm-side-footer{
          flex: 0 0 auto;
          padding: 12px 12px 10px;
          background: #fff;
          border-top: 1px solid rgba(2,6,23,.06);
          border-radius: 12px;
          margin: 10px 6px 0;
        }

        .adm-logout-btn{
          border: none;
          background: transparent;
          cursor: pointer;
          width: 100%;
          text-align: left;
          margin: 8px 0 0;
          padding: 11px 12px;
          border-radius: 12px;
          font-weight: 900;
          color: var(--adm-text);
          display:flex;
          align-items:center;
          gap: 10px;
          transition: background .16s ease, transform .16s ease;
        }
        .adm-logout-btn:hover{
          background: rgba(37, 99, 235, .06);
          transform: translateY(-1px);
        }

        /* ================= DESKTOP SLIM SIDEBAR (ALL OTHER PAGES) ================= */
        .adm-side-slim{
          position: fixed;
          top: 0;
          left: 0;
          width: var(--adm-side-slim);
          height: 100dvh;
          background: var(--adm-card);
          border-right: 1px solid var(--adm-line);
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 14px 10px;
          z-index: 40;
          overflow: hidden;
        }

        .adm-slim-top{
          width: 100%;
          display:flex;
          align-items:center;
          justify-content:center;
          padding: 6px 0 14px;
          border-bottom: 1px solid rgba(2,6,23,.06);
          margin-bottom: 10px;
        }

        .adm-slim-logo{
          width: 44px;
          height: 44px;
          border-radius: 14px;
          overflow: hidden;
          background: #fff;
          box-shadow: 0 10px 18px rgba(37,99,235,.18);
          display:flex;
          align-items:center;
          justify-content:center;
        }

        .adm-slim-logo img{
          width: 100%;
          height: 100%;
          object-fit: cover;
          transform: scale(4.25);
          transform-origin: 50% 24%;
          border-radius: 12px;
        }

        .adm-slim-scroll{
          width: 100%;
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          scrollbar-width: none;
          padding: 6px 0;
          display:flex;
          flex-direction: column;
          align-items:center;
          gap: 8px;
        }
        .adm-slim-scroll::-webkit-scrollbar{ width: 0; height: 0; }

        .adm-slim-item{
          width: 52px;
          height: 48px;
          border-radius: 16px;
          display:flex;
          align-items:center;
          justify-content:center;
          text-decoration:none;
          color: var(--adm-text);
          border: 1px solid transparent;
          font-weight: 900;
          background: transparent;
          transition: background .16s ease, border-color .16s ease, transform .16s ease;
        }
        .adm-slim-item:hover{
          background: rgba(37,99,235,.08);
          transform: translateY(-1px);
        }
        .adm-slim-item.active{
          background: rgba(37,99,235,.12);
          border: 1px solid rgba(37,99,235,.20);
          color: var(--adm-blue);
        }

        .adm-slim-ico{
          width: 32px;
          height: 32px;
          font-size: 16px;
          line-height: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 11px;
        }

        .adm-slim-bottom{
          width: 100%;
          padding-top: 10px;
          border-top: 1px solid rgba(2,6,23,.06);
          display:flex;
          flex-direction: column;
          align-items:center;
          gap: 10px;
        }

        .adm-slim-logout{
          width: 52px;
          height: 48px;
          border-radius: 16px;
          border: 1px solid rgba(2,6,23,.10);
          background: #fff;
          cursor:pointer;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight: 950;
          color: var(--adm-text);
          transition: background .16s ease, border-color .16s ease, transform .16s ease;
        }
        .adm-slim-logout:hover{
          background: rgba(37,99,235,.08);
          border-color: rgba(37,99,235,.18);
          transform: translateY(-1px);
        }

        /* ================= MAIN CONTENT (DESKTOP) ================= */
        .adm-main{
          min-width: 0;
          padding: 18px 22px;
          width: 100%;
        }
        .adm-shell.wide .adm-main{
          padding-left: calc(var(--adm-side-wide) + 22px);
        }
        .adm-shell.slim .adm-main{
          padding-left: calc(var(--adm-side-slim) + 22px);
        }

        /* ================= MOBILE TOPBAR + DRAWER ================= */
        .adm-topbar{ display:none; }

        .adm-drawer,
        .adm-dim{
          display:none;
        }

        @media (max-width: 1099px){
          .adm-side-wide{ display:none !important; }
          .adm-side-slim{ display:none !important; }

          .adm-main{
            padding: 14px !important;
            padding-left: 14px !important;
            width: 100% !important;
            overflow-x: hidden !important;
          }

          .adm-shell{
            overflow-x: hidden !important;
          }

          .adm-main > *{
            max-width: 100% !important;
          }

          .adm-shell.wide .adm-main,
          .adm-shell.slim .adm-main{
            padding-left: 14px !important;
          }

          .adm-topbar{
            display:flex;
            position: sticky;
            top: 0;
            z-index: 60;
            height: 56px;
            align-items:center;
            gap: 10px;
            padding: 10px 12px;
            background: rgba(243,246,251,.85);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid var(--adm-line);
          }

          .adm-hamb{
            width: 44px;
            height: 40px;
            border-radius: 12px;
            border: 1px solid var(--adm-line);
            background: var(--adm-card);
            box-shadow: var(--adm-shadow);
            font-size: 18px;
            font-weight: 900;
            cursor:pointer;
          }

          .adm-title{
            font-weight: 900;
            color: var(--adm-text);
            overflow:hidden;
            white-space:nowrap;
            text-overflow:ellipsis;
          }

          .adm-dim{
            display:block;
            position: fixed;
            inset: 0;
            background: rgba(2,6,23,.45);
            z-index: 80;
          }

          .adm-drawer{
            display:flex;
            position: fixed;
            top: 0;
            left: 0;
            height: 100dvh;
            width: 78%;
            max-width: 320px;
            background: var(--adm-card);
            border-right: 1px solid var(--adm-line);
            z-index: 90;
            box-shadow: 0 18px 40px rgba(2,6,23,.20);
            transform: translateX(-105%);
            transition: transform .2s ease;
            flex-direction:column;
            padding: 14px;
            gap: 10px;
          }

          .adm-drawer.open{ transform: translateX(0); }

          .adm-profile{
            display:flex;
            gap: 12px;
            align-items:center;
            padding: 10px 6px 14px;
            border-bottom: 1px solid var(--adm-line);
            margin-bottom: 6px;
          }

          .adm-avatar{
            width: 42px;
            height: 42px;
            border-radius: 999px;
            background: rgba(37,99,235,.12);
            display:flex;
            align-items:center;
            justify-content:center;
            font-weight: 900;
            color: var(--adm-blue);
          }

          .adm-name{ font-weight: 950; color: var(--adm-text); line-height: 1.1; }
          .adm-sub{ font-size: 12px; color: var(--adm-muted); margin-top: 2px; }

          .adm-item{
            display:flex;
            align-items:center;
            gap: 10px;
            padding: 12px 10px;
            border-radius: 14px;
            text-decoration:none;
            color: var(--adm-text);
            border: 1px solid transparent;
            font-weight: 900;
            transition: background .16s ease, border-color .16s ease, transform .16s ease;
          }

          .adm-item:hover{
            background: rgba(37,99,235,.08);
            transform: translateY(-1px);
          }

          .adm-item.active{
            background: rgba(37,99,235,.12);
            border: 1px solid rgba(37,99,235,.20);
            color: var(--adm-blue);
          }

          .adm-item-ico{
            width: 32px;
            height: 32px;
            display:inline-flex;
            justify-content:center;
            align-items:center;
            font-size: 16px;
            line-height: 1;
            border-radius: 11px;
            flex: 0 0 32px;
          }

          .adm-drawer-spacer{ flex:1; }

          .adm-logout{
            width: 100%;
            padding: 12px 10px;
            border-radius: 14px;
            border: 1px solid rgba(2,6,23,.10);
            background: #fff;
            font-weight: 950;
            cursor:pointer;
            transition: background .16s ease, border-color .16s ease, transform .16s ease;
          }

          .adm-logout:hover{
            background: rgba(37,99,235,.08);
            border-color: rgba(37,99,235,.18);
            transform: translateY(-1px);
          }
        }
      `}</style>

      {/* MOBILE TOP BAR */}
      <div className="adm-topbar">
        <button className="adm-hamb" onClick={() => setOpen(true)} aria-label="Open menu">
          ☰
        </button>
        <div className="adm-title">{pageTitle}</div>
      </div>

      {/* DESKTOP SIDEBAR */}
      {isDashboard ? (
        <aside className="adm-side-wide" aria-label={`Admin navigation for ${displayName}`}>
          <div className="adm-side-logo">
            <div className="adm-logo-wrap">
              <img src="/logo.png" alt="Logo" className="adm-logo-img" />
            </div>
            <div className="adm-logo-text">
              Sort<span>Board</span>
            </div>
          </div>

          <div className="adm-side-scroll">
            <NavLink to="/admin/dashboard" className={sidebarLinkClass}>
              <span className="adm-side-ico adm-tile-blue">
                <FiGrid />
              </span>
              <span>Dashboard</span>
            </NavLink>

            <NavLink to="/admin/calendar" className={sidebarLinkClass}>
              <span className="adm-side-ico adm-tile-amber">
                <FiCalendar />
              </span>
              <span>Calendar</span>
            </NavLink>

            <NavLink to="/admin/schedule" className={sidebarLinkClass}>
              <span className="adm-side-ico adm-tile-cyan">
                <FiEdit3 />
              </span>
              <span>Schedule</span>
            </NavLink>

            <NavLink to="/admin/staff" className={sidebarLinkClass}>
              <span className="adm-side-ico adm-tile-purple">
                <FiUsers />
              </span>
              <span>Staff</span>
            </NavLink>

            <NavLink to="/admin/departments" className={sidebarLinkClass}>
              <span className="adm-side-ico adm-tile-teal">
                <FiTag />
              </span>
              <span>Departments</span>
            </NavLink>

            <NavLink to="/team-schedule" className={sidebarLinkClass}>
              <span className="adm-side-ico adm-tile-indigo">
                <FiClipboard />
              </span>
              <span>Team Schedule</span>
            </NavLink>

            <NavLink to="/admin/attendance-today" className={sidebarLinkClass}>
              <span className="adm-side-ico adm-tile-violet">
                <FaUserMd />
              </span>
              <span>Today's Attendance</span>
            </NavLink>
          </div>

          <div className="adm-side-footer">
            <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
              Logged in as <span style={{ color: "#0f172a", fontWeight: 950 }}>{displayName}</span>
            </div>

            <button
              type="button"
              className="adm-logout-btn"
              onClick={() => {
                logout?.();
                navigate("/login");
              }}
            >
              <span className="adm-side-ico adm-tile-red">
                <FiLogOut />
              </span>
              <span>Logout</span>
            </button>
          </div>
        </aside>
      ) : (
        <aside className="adm-side-slim" aria-label={`Admin navigation (compact) for ${displayName}`}>
          <div className="adm-slim-top">
            <div className="adm-slim-logo" title="SortBoard">
              <img src="/logo.png" alt="Logo" />
            </div>
          </div>

          <div className="adm-slim-scroll">
            <NavLink to="/admin/dashboard" className={slimLinkClass} title="Dashboard" aria-label="Dashboard">
              <span className="adm-slim-ico adm-tile-blue">
                <FiGrid />
              </span>
            </NavLink>

            <NavLink to="/admin/calendar" className={slimLinkClass} title="Calendar" aria-label="Calendar">
              <span className="adm-slim-ico adm-tile-amber">
                <FiCalendar />
              </span>
            </NavLink>

            <NavLink to="/admin/schedule" className={slimLinkClass} title="Schedule" aria-label="Schedule">
              <span className="adm-slim-ico adm-tile-cyan">
                <FiEdit3 />
              </span>
            </NavLink>

            <NavLink to="/admin/staff" className={slimLinkClass} title="Staff" aria-label="Staff">
              <span className="adm-slim-ico adm-tile-purple">
                <FiUsers />
              </span>
            </NavLink>

            <NavLink to="/admin/departments" className={slimLinkClass} title="Departments" aria-label="Departments">
              <span className="adm-slim-ico adm-tile-teal">
                <FiTag />
              </span>
            </NavLink>

            <NavLink to="/team-schedule" className={slimLinkClass} title="Team Schedule" aria-label="Team Schedule">
              <span className="adm-slim-ico adm-tile-indigo">
                <FiClipboard />
              </span>
            </NavLink>

            <NavLink to="/admin/attendance-today" className={slimLinkClass} title="Today's Attendance" aria-label="Today's Attendance">
              <span className="adm-slim-ico adm-tile-violet">
                <FaUserMd />
              </span>
            </NavLink>
          </div>

          <div className="adm-slim-bottom">
            <button
              type="button"
              className="adm-slim-logout"
              title="Logout"
              aria-label="Logout"
              onClick={() => {
                logout?.();
                navigate("/login");
              }}
            >
              <span className="adm-slim-ico adm-tile-red">
                <FiLogOut />
              </span>
            </button>
          </div>
        </aside>
      )}

      {/* MOBILE DRAWER */}
      {open && <div className="adm-dim" onClick={close} />}
      <aside className={`adm-drawer ${open ? "open" : ""}`} aria-label="Admin menu">
        <div className="adm-profile">
          <div className="adm-avatar">{displayName.slice(0, 1).toUpperCase()}</div>
          <div>
            <div className="adm-name">{displayName}</div>
            <div className="adm-sub">Admin</div>
          </div>
        </div>

        <NavLink to="/admin/dashboard" className={({ isActive }) => `adm-item ${isActive ? "active" : ""}`} onClick={close}>
          <span className="adm-item-ico adm-tile-blue">
            <FiGrid />
          </span>
          Dashboard
        </NavLink>

        <NavLink to="/admin/calendar" className={({ isActive }) => `adm-item ${isActive ? "active" : ""}`} onClick={close}>
          <span className="adm-item-ico adm-tile-amber">
            <FiCalendar />
          </span>
          Calendar
        </NavLink>

        <NavLink to="/admin/schedule" className={({ isActive }) => `adm-item ${isActive ? "active" : ""}`} onClick={close}>
          <span className="adm-item-ico adm-tile-cyan">
            <FiEdit3 />
          </span>
          Schedule
        </NavLink>

        <NavLink to="/admin/staff" className={({ isActive }) => `adm-item ${isActive ? "active" : ""}`} onClick={close}>
          <span className="adm-item-ico adm-tile-purple">
            <FiUsers />
          </span>
          Staff
        </NavLink>

        <NavLink to="/admin/departments" className={({ isActive }) => `adm-item ${isActive ? "active" : ""}`} onClick={close}>
          <span className="adm-item-ico adm-tile-teal">
            <FiTag />
          </span>
          Departments
        </NavLink>

        <NavLink to="/team-schedule" className={({ isActive }) => `adm-item ${isActive ? "active" : ""}`} onClick={close}>
          <span className="adm-item-ico adm-tile-indigo">
            <FiClipboard />
          </span>
          Team Schedule
        </NavLink>

        <NavLink to="/admin/attendance-today" className={({ isActive }) => `adm-item ${isActive ? "active" : ""}`} onClick={close}>
          <span className="adm-item-ico adm-tile-violet">
            <FaUserMd />
          </span>
          Today's Attendance
        </NavLink>

        <div className="adm-drawer-spacer" />

        <button
          className="adm-logout"
          onClick={() => {
            close();
            logout?.();
            navigate("/login");
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", marginRight: 8 }}>
            <span className="adm-item-ico adm-tile-red">
              <FiLogOut />
            </span>
          </span>
          Logout
        </button>
      </aside>

      {/* PAGE CONTENT */}
      <main className="adm-main">{children}</main>
    </div>
  );
}